import {
    T, all, always, compose, concat, cond, equals, filter,
    flatten, includes, keys, lensPath, map,
    mergeDeepWithKey, not, omit, path, pathOr, reduce, set
} from "ramda";
import { isEmptyOrNil, isNotEmpty, propIsEmpty, propIsNotEmpty, returnIsNotEmptyOrTrue } from "src/utilities/hasValue";
import { isObject } from "src/utilities/types";
import {
    API_RESOURCE_BORROWERS,
    API_RESOURCE_ASSETS,
    API_RESOURCE_PROPERTIES,
    API_RESOURCE_LENDING_TERMS,
    API_RESOURCE_USER_ACCOUNTS,
    API_RESOURCE_DECLARATIONS,
    API_RESOURCE_LOAN_STATUS,
    API_RESOURCE_BUSINESS,
    API_RESOURCE_EMPLOYMENTS,
    API_RESOURCE_BUSINESS_INCOMES,
    API_RESOURCE_ADDITIONAL_LOAN,
    API_RESOURCE_REAL_ESTATE_ASSET,
    API_RESOURCE_MONTHLY_INCOMES,
    API_RESOURCE_LIABILITIES,
    API_RESOURCE_LOANAPP_ROLES,
    API_RESOURCE_WORKFLOW_INTENTS,
    API_RESOURCE_COLLATERAL,
    API_RESOURCE_LOAN_APPS,
    ApiResource,
    doNothing,
    doNothingPass,
    doNothingFail,
    LOAN_APP
} from "./constants";
import { createAssets, deleteAsset, upsertAssets, updateAssets } from 'src/store/assets/assets.requests';
import { updateBorrower, upsertBorrower, upsertBorrowerUpdate } from 'src/store/borrower/borrower.requests';
import { updateBusiness, upsertBusiness } from 'src/store/business/business.requests';
import { updateProperties, upsertProperties } from 'src/store/properties/properties.requests';
import {
    createEmployment,
    deleteEmployment,
    updateEmployment,
    upsertEmployments,
} from 'src/store/employment/employment.requests';
import { updateLendingTerm } from 'src/store/lendingTerms/lendingterms.requests';
import { updateAccount } from 'src/store/account/account.requests';
import { updateLoanStatus, upsertLoanStatus } from 'src/store/loanStatus/loan_status.requests';
import { } from 'src/store/borrower/borrower.requests';
import { updateDeclaration, upsertDeclaration, upsertDeclarationUpdate } from 'src/store/declaration/declaration.requests';
import { upsertEmploymentUpdate } from 'src/store/employment/employment.requests';
import { upsertAssetsUpdate } from 'src/store/assets/assets.requests';
import { upsertLendingTermUpdate } from 'src/store/lendingTerms/lendingterms.requests';
import { upsertLoanStatusUpdate } from 'src/store/loanStatus/loan_status.requests';
import { createRealEstateAsset, deleteRealEstateAssets, updateRealEstateAssets, upsertRealEstateAsset, upsertRealEstateAssetUpdate } from 'src/store/realEstateAssets/realEstateAssets.requests';
import { createCreditReport, updateLiability, upsertCreditReport, upsertLiabilitiesUpdate } from 'src/store/liabilities/liabilities.requests';
import {
    updateUserSpecificIntent,
    upsertUserSpecificIntentUpdate,
    upsertUserSpecificIntents
} from 'src/store/siteConfigurator/userSpecificIntent/userSpecificIntent.requests';
import { upsertBusinessUpdate } from 'src/store/business/business.requests';
import {
    createMonthlyIncomes,
    deleteMonthlyIncome,
    updateMonthlyIncome,
    upsertMonthlyIncome,
    upsertMonthlyIncomeUpdate
} from 'src/store/monthlyIncome/monthlyIncome.requests';
import { upsertBorrowerAccountUpdate, upsertBorrowerAccounts } from 'src/store/applicantAccounts/accounts.requests';
import { upsertLoanAppRole, upsertLoanAppRoleUpdate } from 'src/store/loanAppRole/loanrole.requests';
import { batchLoanApp } from "src/services/loans/loans";
import { ThunkDispatch } from "src/store/types";
import { mapIndexed } from "src/utilities/visualFieldsMapper";
import { createCollateral, deleteCollateral, updateCollateral, upsertCollateralUpdate, upsertCollaterals } from "src/store/collateral/collateral.requests";
import { deleteAdditionalLoan } from "./additionalLoans/additionalLoans";
import { createAdditionalLoanReq, updateAdditionalLoan } from "src/store/additionalLoans/addLoan.requests";
import { updateSingleLoanAppInStore, updateLoanApplication } from "src/store/loanapp/loanapp.requests";

const LOANAPP_PATCH_API_RESOURCES = [
    API_RESOURCE_BORROWERS,
    API_RESOURCE_ASSETS,
    API_RESOURCE_PROPERTIES,
    API_RESOURCE_LENDING_TERMS,
    API_RESOURCE_USER_ACCOUNTS,
    API_RESOURCE_DECLARATIONS,
    API_RESOURCE_LOAN_STATUS,
    API_RESOURCE_BUSINESS,
    API_RESOURCE_EMPLOYMENTS,
    API_RESOURCE_BUSINESS_INCOMES,
    API_RESOURCE_ADDITIONAL_LOAN,
    API_RESOURCE_REAL_ESTATE_ASSET,
    API_RESOURCE_MONTHLY_INCOMES,
    API_RESOURCE_LIABILITIES,
    API_RESOURCE_LOANAPP_ROLES,
    API_RESOURCE_WORKFLOW_INTENTS,
    API_RESOURCE_COLLATERAL,
]


export interface ServiceData {
    payload: any;
    kind: ApiResource;
    isDelete?: boolean;
    isNew?: boolean;
    isIndividualRequest?: boolean;
}

type RequestMap = Record<ApiResource, any>;

const updateRequestMap = (data?: any): RequestMap => {
    return {
        // TODO: have to change the upsert support same schema like items then we can get rid of these multiple payload 
        [API_RESOURCE_PROPERTIES]: () => upsertProperties({ items: [pathOr({}, [API_RESOURCE_PROPERTIES], data)] }),
        [API_RESOURCE_LENDING_TERMS]: () => upsertLendingTermUpdate(pathOr({}, [API_RESOURCE_LENDING_TERMS], data)),

        [API_RESOURCE_WORKFLOW_INTENTS]: () => map(upsertUserSpecificIntentUpdate, pathOr([], [API_RESOURCE_WORKFLOW_INTENTS], data)),
        [API_RESOURCE_BORROWERS]: () => map(upsertBorrowerUpdate, pathOr([], [API_RESOURCE_BORROWERS], data)),
        [API_RESOURCE_USER_ACCOUNTS]: () => map(upsertBorrowerAccountUpdate, pathOr([], [API_RESOURCE_USER_ACCOUNTS], data)),
        [API_RESOURCE_DECLARATIONS]: () => map(upsertDeclarationUpdate, pathOr([], [API_RESOURCE_DECLARATIONS], data)),
        [API_RESOURCE_EMPLOYMENTS]: () => map(upsertEmploymentUpdate, pathOr([], [API_RESOURCE_EMPLOYMENTS], data)),
        [API_RESOURCE_ASSETS]: () => map(upsertAssetsUpdate, pathOr([], [API_RESOURCE_ASSETS], data)),
        [API_RESOURCE_LOAN_STATUS]: () => map(upsertLoanStatusUpdate, pathOr([], [API_RESOURCE_LOAN_STATUS], data)),
        [API_RESOURCE_REAL_ESTATE_ASSET]: () => map(upsertRealEstateAssetUpdate, pathOr([], [API_RESOURCE_REAL_ESTATE_ASSET], data)),
        [API_RESOURCE_LIABILITIES]: () => map(upsertLiabilitiesUpdate, pathOr([], [API_RESOURCE_LIABILITIES], data)),
        [API_RESOURCE_BUSINESS]: () => map(upsertBusinessUpdate, pathOr([], [API_RESOURCE_BUSINESS], data)),
        [API_RESOURCE_MONTHLY_INCOMES]: () => map(upsertMonthlyIncomeUpdate, pathOr([], [API_RESOURCE_MONTHLY_INCOMES], data)),
        [API_RESOURCE_COLLATERAL]: () => map(upsertCollateralUpdate, pathOr([], [API_RESOURCE_COLLATERAL], data)),
        [API_RESOURCE_LOANAPP_ROLES]: () => map(upsertLoanAppRoleUpdate, pathOr([], [API_RESOURCE_LOANAPP_ROLES], data)),
        [API_RESOURCE_BUSINESS_INCOMES]: () => doNothingPass({}),
        [API_RESOURCE_ADDITIONAL_LOAN]: () => doNothingPass({}),
    };
};


const describeRequestMap = (data?: any): RequestMap => {
    return {
        // TODO: have to change the upsert support same schema like items then we can get rid of these multiple payload 
        [API_RESOURCE_PROPERTIES]: () => upsertProperties({ items: [pathOr({}, [API_RESOURCE_PROPERTIES], data)] }),
        [API_RESOURCE_LENDING_TERMS]: () => upsertLendingTermUpdate(pathOr({}, [API_RESOURCE_LENDING_TERMS], data)),

        [API_RESOURCE_WORKFLOW_INTENTS]: () => upsertUserSpecificIntents({ items: pathOr([], [API_RESOURCE_WORKFLOW_INTENTS], data) }),
        [API_RESOURCE_BORROWERS]: () => upsertBorrower({ items: pathOr([], [API_RESOURCE_BORROWERS], data) }),
        [API_RESOURCE_USER_ACCOUNTS]: () => upsertBorrowerAccounts({ items: pathOr([], [API_RESOURCE_USER_ACCOUNTS], data) }),
        [API_RESOURCE_DECLARATIONS]: () => upsertDeclaration({ items: pathOr([], [API_RESOURCE_DECLARATIONS], data) }),
        [API_RESOURCE_EMPLOYMENTS]: () => upsertEmployments({ items: pathOr([], [API_RESOURCE_EMPLOYMENTS], data) }),
        [API_RESOURCE_ASSETS]: () => upsertAssets({ items: pathOr([], [API_RESOURCE_ASSETS], data) }),
        [API_RESOURCE_LOAN_STATUS]: () => upsertLoanStatus({ items: pathOr([], [API_RESOURCE_LOAN_STATUS], data) }),
        [API_RESOURCE_REAL_ESTATE_ASSET]: () => upsertRealEstateAsset({ items: pathOr([], [API_RESOURCE_REAL_ESTATE_ASSET], data) }),
        [API_RESOURCE_LIABILITIES]: () => upsertCreditReport({ items: pathOr([], [API_RESOURCE_LIABILITIES], data) }),
        [API_RESOURCE_BUSINESS]: () => upsertBusiness({ items: pathOr([], [API_RESOURCE_BUSINESS], data) }),
        [API_RESOURCE_MONTHLY_INCOMES]: () => upsertMonthlyIncome({ items: pathOr([], [API_RESOURCE_MONTHLY_INCOMES], data) }),
        [API_RESOURCE_COLLATERAL]: () => upsertCollaterals({ items: pathOr([], [API_RESOURCE_COLLATERAL], data) }),
        [API_RESOURCE_LOANAPP_ROLES]: () => upsertLoanAppRole({ items: pathOr([], [API_RESOURCE_LOANAPP_ROLES], data) }),
        [API_RESOURCE_BUSINESS_INCOMES]: () => doNothingPass({}),
        [API_RESOURCE_ADDITIONAL_LOAN]: () => doNothingPass({}),
    };
};
const mutateLoanAppReduxStore = (loanApp: any) => () => updateSingleLoanAppInStore(loanApp)



interface IResourceEntity {
    //TO_DO add a valid response type for reducer action methods
    fnInsert: () => (data: any) => Promise<any>;
    fnUpdate: () => (data: any) => Promise<any>;
    fnDelete: () => (data: any) => Promise<any>;
}

class BaseAPIContextDispatcher {
    private apiresource: ApiResource;
    private updater: IResourceEntity;
    constructor(apiresource: ApiResource) {
        this.apiresource = apiresource;
        this.updater = this.requestEntity();
    }

    requestEntity() {
        return new APIResourceEntity(this.apiresource);
    }

    fnInsert = () => {
        return this.updater.fnInsert();
    };
    fnUpdate = () => {
        return this.updater.fnUpdate();
    };
    fnDelete = () => {
        return this.updater.fnDelete();
    };
}



class APIResourceEntity implements IResourceEntity {
    private apiResource: ApiResource;
    constructor(apiResource: ApiResource) {
        this.apiResource = apiResource;
    }

    fnInsert = () => {
        return cond([
            [equals(API_RESOURCE_EMPLOYMENTS), () => (createEmployment)],
            [equals(API_RESOURCE_MONTHLY_INCOMES), always(createMonthlyIncomes)],
            [equals(API_RESOURCE_REAL_ESTATE_ASSET), always(createRealEstateAsset)],
            [equals(API_RESOURCE_COLLATERAL), always(createCollateral)],
            [equals(API_RESOURCE_ASSETS), always(createAssets)],
            [equals(API_RESOURCE_ADDITIONAL_LOAN), always(createAdditionalLoanReq)],
            [equals(API_RESOURCE_LIABILITIES), always(createCreditReport)],
            [equals(API_RESOURCE_COLLATERAL), always(createCollateral)],
            [equals('noop_pass'), always(doNothingPass)],
            [equals('noop_fail'), always(doNothingFail)],
            [T, always(doNothingPass)],
        ])(this.apiResource);
    };

    fnUpdate = () => {
        return cond([
            [equals(API_RESOURCE_EMPLOYMENTS), always(updateEmployment)],
            [equals(API_RESOURCE_DECLARATIONS), always(updateDeclaration)],
            [equals(API_RESOURCE_USER_ACCOUNTS), always(updateAccount)],
            [equals(API_RESOURCE_BORROWERS), always(updateBorrower)],
            [equals(API_RESOURCE_ASSETS), always(updateAssets)],
            [equals(API_RESOURCE_BUSINESS), always(updateBusiness)],
            [equals(API_RESOURCE_COLLATERAL), always(updateCollateral)],
            [equals(API_RESOURCE_PROPERTIES), always(updateProperties)],
            [equals(API_RESOURCE_LENDING_TERMS), always(updateLendingTerm)],
            [equals(API_RESOURCE_LOAN_STATUS), always(updateLoanStatus)],
            [equals(API_RESOURCE_WORKFLOW_INTENTS), always(updateUserSpecificIntent)],
            [equals(API_RESOURCE_LIABILITIES), always(updateLiability)],
            [equals(API_RESOURCE_MONTHLY_INCOMES), always(updateMonthlyIncome)],
            [equals(API_RESOURCE_REAL_ESTATE_ASSET), always(updateRealEstateAssets)],
            [equals(API_RESOURCE_ADDITIONAL_LOAN), always(updateAdditionalLoan)],
            [equals(API_RESOURCE_LOAN_APPS), always(updateLoanApplication)],
            [equals('noop_pass'), always(doNothingPass)],
            [equals('noop_fail'), always(doNothingFail)],
            [T, always(doNothingPass)],
        ])(this.apiResource);
    };

    fnDelete = () => {
        return cond([
            [equals(API_RESOURCE_EMPLOYMENTS), always(deleteEmployment)],
            [equals(API_RESOURCE_MONTHLY_INCOMES), always(deleteMonthlyIncome)],
            [equals(API_RESOURCE_REAL_ESTATE_ASSET), always(deleteRealEstateAssets)],
            [equals(API_RESOURCE_ASSETS), always(deleteAsset)],
            [equals(API_RESOURCE_ADDITIONAL_LOAN), always(deleteAdditionalLoan)],
            [equals(API_RESOURCE_COLLATERAL), always(deleteCollateral)],
            [equals('noop_pass'), always(doNothingPass)],
            [equals('noop_fail'), always(doNothingFail)],
            [T, always(doNothingPass)],
        ])(this.apiResource);
    };
}


class PayloadEvaluator {
    serviceData: any;
    response: any;

    constructor(serviceData: ServiceData) {
        this.serviceData = serviceData;
    }

    payload() {
        return pathOr({}, ['payload'], this.serviceData)
    }

    kind() {
        return pathOr('', ['kind'], this.serviceData)
    }

    uid() {
        return pathOr('', ['payload', 'id'], this.serviceData)
    }

    isIndividual() {
       const isPatchSupported = cond([
            [() => includes(this.kind(), LOANAPP_PATCH_API_RESOURCES), always(true)],
            [equals(API_RESOURCE_LOAN_APPS), always(false)]
          ])(this.kind())
        
        // Since we don't support Delete on Batch update we consider delete as individual request
        return (pathOr(false, ['isIndividualRequest'], this.serviceData) || this.isDelete() || not(isPatchSupported))
    }

    isDelete() {
        return isNotEmpty(this.uid()) && pathOr(false, ['isDelete'], this.serviceData)
    }

    isCreate() {
        return returnIsNotEmptyOrTrue(
            pathOr(false, ['isNew'], this.serviceData),
            true,
            false
        )
    }

    dispatcher() {
        const apiDispatcher = new BaseAPIContextDispatcher(this.kind() as ApiResource)
        const dispatchFn = cond([
            [() => this.isCreate(), always(apiDispatcher.fnInsert())],
            [() => this.isDelete(), always(() => apiDispatcher.fnDelete()(this.uid()))],
            [T, always(apiDispatcher.fnUpdate())]
        ])()
        return dispatchFn
    }

    isSchemaValid() {
        return isNotEmpty(this.payload()) && isObject(this.payload()) && isNotEmpty(this.kind())
    }

    setResponse(response: any) {
        this.response = response
    }
}


class LoanAppReduxLoader {
    loanApp: any;
    constructor(loanApp: any) {
        this.loanApp = loanApp
    }

    async load(
        dispatch: ThunkDispatch,
        doneCallback: () => void = doNothing
    ) {
        const spec = filter(isNotEmpty, pathOr({}, ['spec'], this.loanApp))
        const apiResources = keys(spec)
        const requestMaps = updateRequestMap(spec)
        const requestFns = filter(isNotEmpty, map(
            (apiResource: string) => path([apiResource], requestMaps),
            apiResources)
        );

        if (not(isNotEmpty(requestFns))) {
            doneCallback && doneCallback();
            return;
        }

        const dispatches = map((requestFn: any) => map(
            (action: any) => dispatch<any>(action), flatten([requestFn()])
        ), requestFns)
        doneCallback && doneCallback();
        return dispatches
    }

    async describe(
        dispatch: ThunkDispatch,
        doneCallback: () => void = doNothing
    ) {
        const spec = filter(isNotEmpty, pathOr({}, ['spec'], this.loanApp))
        const apiResources = keys(spec)
        const requestMaps = describeRequestMap(spec)
        const requestFns = filter(isNotEmpty, map(
            (apiResource: string) => path([apiResource], requestMaps),
            apiResources)
        );

        if (not(isNotEmpty(requestFns))) {
            doneCallback && doneCallback();
            return;
        }

        const dispatches = map((requestFn: any) => map(
            (action: any) => dispatch<any>(action), flatten([requestFn()])
        ), flatten([requestFns, mutateLoanAppReduxStore(this.loanApp)]))
        doneCallback && doneCallback();
        return dispatches
    }
}

/** 
merge array of elements from given list of objects
@param keys { string[] } array of string to get arrays to be merged from give list objects
@param data { objects[] } array of objects which contains the list to be merged.
let concatValues = (k, l, r) => k == 'b' ? R.concat(l, r) : r
example:  mergeObjectsWithKey(concatValues)([
  {a: [10]},
  {b: [10]},
  {a: [50], c: 20, b: [20],  "sub": {"s": "d", "a": 10, "logic": true}}
])
mergeObjectsWithKey => returns {"a": [50], "b": [10, 20], "c": 20, "sub": {"a": 10, "logic": true, "s": "d"}}
*/

const mergeObjectsWithKey = (concatFn: (k: string, l: any, r: any) => any) => {
    return reduce(mergeDeepWithKey(concatFn), {})
}

const singleObjectResources = [
    API_RESOURCE_LENDING_TERMS,
    API_RESOURCE_PROPERTIES]

const concatValues = (key: string, l: any, r: any) => includes(key, singleObjectResources) ? r : concat(l, r)


class IndividualAPIReqester {
    dataEvaluators: PayloadEvaluator[];

    constructor(dataEvaluators: PayloadEvaluator[]) {
        this.dataEvaluators = dataEvaluators
    }



    async request(dispatch: any) {
        if (isEmptyOrNil(this.dataEvaluators)) {
            return []
        }

        const dispatchers = map(async (p: PayloadEvaluator) => {
            const updFn = p.dispatcher()
            return dispatch(updFn(p.payload()));
        }, this.dataEvaluators)

        return Promise.all(flatten([dispatchers])).then((res) => {
            return res
        })
    }

}

class BatchAPIBuilder {
    dataEvaluators: PayloadEvaluator[];
    loanAppId: string;

    constructor(loanAppId: string, dataEvaluators: PayloadEvaluator[]) {
        this.loanAppId = loanAppId;
        this.dataEvaluators = dataEvaluators
    }



    build() {

        const resources = mergeObjectsWithKey(concatValues)(map((de: PayloadEvaluator) => {
            // Make batch API's object into array
            const payload = includes(de.kind(), singleObjectResources) ?
                de.payload() :
                flatten([de.payload()])

            return {
                [de.kind()]: payload
            }
        }, this.dataEvaluators))
        //
        return omit([LOAN_APP])(resources)
    }

    makeResponsePayload(request: any, response: any) {
        const responseSpec = filter(isNotEmpty, pathOr({}, ['spec'], response))
        if (isEmptyOrNil(responseSpec)) {
            return request
        }

        const mergedResponse = mergeObjectsWithKey(concatValues)(map((apiResource: string) => {
            const resourceResponse = pathOr([], [apiResource], responseSpec)
            const resourceRequests = pathOr([], [apiResource], request)
            const updatePayloads = filter(propIsNotEmpty('id'), resourceRequests)
            if (includes(apiResource, singleObjectResources) && isNotEmpty(resourceRequests)) {
                return {
                    [apiResource]: resourceRequests
                }
            }

            // API will send same sequence as we used in request payload
            const createPayloads = mapIndexed((payload: any, index: number) => {
                return compose(
                    set(lensPath(['id']), pathOr('', [index, 'id'], resourceResponse)),
                    set(lensPath(['created_at']), pathOr('', [index, 'created_at'], resourceResponse))
                )(payload)
            }, filter(propIsEmpty('id'), resourceRequests));

            return {
                [apiResource]: flatten([createPayloads, updatePayloads])
            }
        }, keys(request)))

        return mergedResponse
    }

    async requestRedux(
        dispatch: ThunkDispatch,
        doneCallback: () => void = doNothing,
    ) {
        return new Promise(async (resolve, reject) => {
            try {
                const request = this.build()
                if (isEmptyOrNil(this.loanAppId) || isEmptyOrNil(request)) {
                    return resolve(request)
                }

                const response = await batchLoanApp(this.loanAppId, request)
                const payload = this.makeResponsePayload(request, response)
                const updatedData = {
                    spec: payload
                }

                const reduxLoader = new LoanAppReduxLoader(updatedData)
                await reduxLoader.load(dispatch, doneCallback)
                resolve(payload)
            } catch (e: any) {
                reject(e)
            }
        });
    }

    async request() {
        return batchLoanApp(this.loanAppId, this.build())
    }
}


/**
 * ServiceAPIBuilder class is used to request Loan application api resources 
 * it can be individual calls or batch calls based on the given request it will do the process 
*/
class ServiceAPIBuilder {
    servicesData: ServiceData[];
    dataEvaluators: PayloadEvaluator[];
    loanAppId: string;
    batchAPIBuilder: BatchAPIBuilder;
    serviceRequester: IndividualAPIReqester;

    // If the flow is Not authenticated we will handle all request using individual service requestion
    // since individual redux will handle session instead of service call upon no auth
    constructor(loanAppId: string, servicesData: ServiceData[]) {
        this.loanAppId = loanAppId;
        this.servicesData = filter(propIsNotEmpty('kind'), servicesData) as any;

        this.dataEvaluators = map((s: ServiceData) => new PayloadEvaluator(s), this.servicesData);

        const batchEvaluators = filter((s: PayloadEvaluator) => not(s.isIndividual()), this.dataEvaluators)

        this.batchAPIBuilder = new BatchAPIBuilder(
            loanAppId,
            isEmptyOrNil(loanAppId) ? [] : batchEvaluators
        )
        const serviceEvaluators = filter((s: PayloadEvaluator) => s.isIndividual(), this.dataEvaluators)
        this.serviceRequester = new IndividualAPIReqester(
            isEmptyOrNil(loanAppId) ? this.dataEvaluators : serviceEvaluators
        )
    }

    isSchemaValid() {
        return all((s: PayloadEvaluator) => s.isSchemaValid(), this.dataEvaluators)
    }

    async requestRedux(
        dispatch: ThunkDispatch,
        doneCallback: () => void = doNothing,
    ) {
        return new Promise(async (resolve, reject) => {
            try {
                await this.serviceRequester.request(dispatch)
                await this.batchAPIBuilder.requestRedux(dispatch)
                doneCallback && doneCallback()
                resolve({})
            } catch (e: any) {
                reject(e)
            }
        });
    }


    async request(
        dispatch: ThunkDispatch,
        doneCallback: () => void = doNothing,
        failCallback: (err: any) => void = doNothing
    ) {
        return Promise.all([this.batchAPIBuilder.request(), this.serviceRequester.request(dispatch)]).
            then((resp: any) => {
                doneCallback && doneCallback()
            }).
            catch((_e: any) => {
                failCallback && failCallback(_e)
            })
    }
}


export {
    ServiceAPIBuilder,
    LoanAppReduxLoader
}